import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Grid,
  Paper,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { adminApi } from '../api';

const empty = {
  calendarEnabled: false,
  googleCalendarId: '',
  appointmentDuration: 30,
  slotStartHour: 9,
  slotEndHour: 18,
  availableDays: 'Lunedì, Martedì, Mercoledì, Giovedì, Venerdì',
  googleCredentialsJson: '',
  googleOauthClientId: '',
  googleOauthClientSecret: '',
  emailFrom: '',
  userEmailSubject: '',
  userEmailBody: '',
  adminEmailSubject: '',
  adminEmailBody: '',
  evolutionEnabled: false,
  whatsappEnabled: false,
  whatsappMessageBody: '',
};

export default function AppointmentsPage() {
  const [form, setForm] = useState(empty);
  const [hasGoogleCredentials, setHasGoogleCredentials] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleOauthEmail, setGoogleOauthEmail] = useState('');
  const [oauthConfigured, setOauthConfigured] = useState(false);
  const [hasGoogleOauthSecret, setHasGoogleOauthSecret] = useState(false);
  const [googleRedirectUri, setGoogleRedirectUri] = useState('');
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [waStatus, setWaStatus] = useState(null);
  const [waLoading, setWaLoading] = useState(false);

  const apply = (data) => {
    setHasGoogleCredentials(Boolean(data.hasGoogleCredentials));
    setGoogleConnected(Boolean(data.googleConnected));
    setGoogleOauthEmail(data.googleOauthEmail || '');
    setOauthConfigured(Boolean(data.oauthConfigured));
    setHasGoogleOauthSecret(Boolean(data.hasGoogleOauthSecret));
    setGoogleRedirectUri(data.googleRedirectUri || '');
    setForm({
      calendarEnabled: Boolean(data.calendarEnabled),
      googleCalendarId: data.googleCalendarId || '',
      appointmentDuration: data.appointmentDuration || 30,
      slotStartHour: data.slotStartHour ?? 9,
      slotEndHour: data.slotEndHour ?? 18,
      availableDays: Array.isArray(data.availableDays) ? data.availableDays.join(', ') : '',
      googleCredentialsJson: '',
      googleOauthClientId: data.googleOauthClientId || '',
      googleOauthClientSecret: '',
      emailFrom: data.emailFrom || '',
      userEmailSubject: data.userEmailSubject || '',
      userEmailBody: data.userEmailBody || '',
      adminEmailSubject: data.adminEmailSubject || '',
      adminEmailBody: data.adminEmailBody || '',
      evolutionEnabled: Boolean(data.evolutionEnabled),
      whatsappEnabled: Boolean(data.whatsappEnabled),
      whatsappMessageBody: data.whatsappMessageBody || '',
    });
  };

  useEffect(() => {
    adminApi.getAppointments().then(apply).catch((err) => setError(err.message));
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'ok') {
      setMessage('Google Calendar collegato. Puoi abilitare le prenotazioni.');
    } else if (params.get('google') === 'error') {
      setError(params.get('reason') || 'Collegamento Google non riuscito.');
    }
    if (params.has('google')) {
      window.history.replaceState({}, '', '/admin/appuntamenti');
    }
  }, []);

  useEffect(() => {
    if (!googleConnected) {
      setGoogleCalendars([]);
      return undefined;
    }
    adminApi.getGoogleCalendars()
      .then((data) => setGoogleCalendars(data.calendars || []))
      .catch(() => setGoogleCalendars([]));
    return undefined;
  }, [googleConnected]);

  useEffect(() => {
    let cancelled = false;
    const load = async (refreshQr) => {
      try {
        const data = await adminApi.getWhatsAppStatus(refreshQr);
        if (!cancelled) setWaStatus(data);
      } catch (err) {
        if (!cancelled) {
          setWaStatus({
            reachable: false,
            connected: false,
            error: err.message,
            status: 'offline',
          });
        }
      }
    };
    load(false);
    const waitingQr = !waStatus?.connected && (Boolean(waStatus?.qr) || waStatus?.status === 'connecting');
    const timer = setInterval(() => load(Boolean(waitingQr)), waitingQr ? 2500 : 8000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [waStatus?.qr, waStatus?.connected]);

  const setField = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  const setSwitch = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.checked }));

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      const saved = await adminApi.saveAppointments({
        ...form,
        appointmentDuration: Number(form.appointmentDuration) || 30,
        slotStartHour: Number(form.slotStartHour) || 9,
        slotEndHour: Number(form.slotEndHour) || 18,
        availableDays: form.availableDays.split(',').map((d) => d.trim()).filter(Boolean),
        googleCredentialsJson: form.googleCredentialsJson.trim() || undefined,
        googleOauthClientId: form.googleOauthClientId,
        googleOauthClientSecret: form.googleOauthClientSecret.trim() || undefined,
      });
      apply(saved);
      setMessage('Configurazione salvata.');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box component="form" onSubmit={handleSave}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
        Contatti
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Calendario Google e chatbot su WhatsApp (Evolution API).
      </Typography>
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Calendario appuntamenti</Typography>
        <FormControlLabel
          control={<Switch checked={form.calendarEnabled} onChange={setSwitch('calendarEnabled')} />}
          label="Abilita prenotazione da Google Calendar"
        />
        <Alert severity="info" sx={{ mb: 2 }}>
          Per il cliente basta <b>Collega Google Calendar</b> e accedere col Gmail del calendario.
          Una tantum, per questa piattaforma, serve un Client ID OAuth da Google Cloud (non il JSON del service account).
        </Alert>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Google OAuth Client ID"
              value={form.googleOauthClientId}
              onChange={setField('googleOauthClientId')}
              fullWidth
              helperText="API e servizi → Credenziali → ID client OAuth (applicazione web)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Google OAuth Client Secret"
              type="password"
              value={form.googleOauthClientSecret}
              onChange={setField('googleOauthClientSecret')}
              fullWidth
              helperText={hasGoogleOauthSecret ? 'Secret già salvato: lascia vuoto per non cambiarlo.' : 'Incolla il secret, poi Salva, poi Collega.'}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              URI di reindirizzamento da inserire su Google Cloud:{' '}
              <code>{googleRedirectUri || 'http://localhost:3001/admin/google/callback'}</code>
            </Typography>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            label={
              googleConnected
                ? `Collegato${googleOauthEmail ? ` · ${googleOauthEmail}` : ''}`
                : oauthConfigured
                  ? 'Non collegato'
                  : 'OAuth non configurato'
            }
            color={googleConnected ? 'success' : 'default'}
          />
          <Button
            type="button"
            variant="contained"
            onClick={async () => {
              setError('');
              try {
                const data = await adminApi.getGoogleAuthUrl();
                if (!data.configured || !data.url) {
                  setError(data.error || 'Salva prima Client ID e Client Secret, poi riprova.');
                  return;
                }
                window.location.href = data.url;
              } catch (err) {
                setError(err.message);
              }
            }}
          >
            Collega Google Calendar
          </Button>
          {googleConnected && (
            <Button
              type="button"
              color="warning"
              onClick={async () => {
                if (!window.confirm('Scollegare Google Calendar?')) return;
                try {
                  const saved = await adminApi.saveAppointments({ ...form, clearGoogleOauth: true, googleCredentialsJson: undefined });
                  apply(saved);
                  setMessage('Google Calendar scollegato.');
                } catch (err) {
                  setError(err.message);
                }
              }}
            >
              Scollega
            </Button>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {googleCalendars.length > 0 ? (
              <TextField
                select
                label="Calendario"
                value={form.googleCalendarId || 'primary'}
                onChange={setField('googleCalendarId')}
                fullWidth
              >
                {googleCalendars.map((calendar) => (
                  <MenuItem key={calendar.id} value={calendar.id}>
                    {calendar.summary}{calendar.primary ? ' (principale)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                label="Google Calendar ID"
                value={form.googleCalendarId}
                onChange={setField('googleCalendarId')}
                fullWidth
                helperText="Dopo il collegamento di solito è «primary»."
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Durata slot (minuti)" type="number" value={form.appointmentDuration} onChange={setField('appointmentDuration')} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Ora inizio" type="number" value={form.slotStartHour} onChange={setField('slotStartHour')} fullWidth />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField label="Ora fine" type="number" value={form.slotEndHour} onChange={setField('slotEndHour')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Giorni disponibili" value={form.availableDays} onChange={setField('availableDays')} fullWidth helperText="Es. Lunedì, Martedì, Mercoledì" />
          </Grid>
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2">Metodo avanzato: JSON service account (opzionale)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Serve solo se non usi &quot;Collega Google Calendar&quot;. Non è il percorso consigliato per il cliente.
                </Typography>
                <TextField
                  label="JSON service account Google"
                  value={form.googleCredentialsJson}
                  onChange={setField('googleCredentialsJson')}
                  fullWidth
                  multiline
                  minRows={4}
                  helperText="Lascia vuoto per non modificare quello già salvato."
                />
                <Button type="button" component="label" sx={{ mt: 1, mr: 1 }}>
                  Carica file JSON
                  <input
                    type="file"
                    accept="application/json,.json"
                    hidden
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      if (!file) return;
                      const text = await file.text();
                      setForm((prev) => ({ ...prev, googleCredentialsJson: text }));
                    }}
                  />
                </Button>
                {hasGoogleCredentials && !googleConnected && (
                  <Button
                    type="button"
                    color="warning"
                    onClick={async () => {
                      if (!window.confirm('Rimuovere le credenziali Google JSON?')) return;
                      try {
                        const saved = await adminApi.saveAppointments({ ...form, clearGoogleCredentials: true, googleCredentialsJson: undefined });
                        apply(saved);
                        setMessage('Credenziali Google rimosse.');
                      } catch (err) {
                        setError(err.message);
                      }
                    }}
                  >
                    Rimuovi JSON
                  </Button>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>WhatsApp (Evolution API)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Il chatbot risponde sul WhatsApp Business del cliente. Evolution API gira in Docker su
          questa macchina (porta 8080): avvia <code>docker compose up -d</code> dalla cartella del progetto,
          poi collega il numero scansionando il QR.
        </Typography>
        <FormControlLabel
          control={<Switch checked={form.evolutionEnabled} onChange={setSwitch('evolutionEnabled')} />}
          label="Abilita il chatbot su WhatsApp"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', my: 1 }}>
          <Chip
            label={
              !waStatus?.reachable
                ? 'Evolution API spenta'
                : waStatus.connected
                  ? `Connesso${waStatus.number ? ` · ${waStatus.number}` : ''}`
                  : waStatus.qr
                    ? 'In attesa di scansione QR'
                    : 'Disconnesso'
            }
            color={!waStatus?.reachable ? 'default' : waStatus.connected ? 'success' : 'warning'}
          />
          {waStatus?.apiUrl && (
            <Typography variant="caption" color="text.secondary">
              {waStatus.apiUrl}
            </Typography>
          )}
        </Box>
        {waStatus?.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>{waStatus.error}</Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            type="button"
            variant="contained"
            disabled={waLoading}
            onClick={async () => {
              setWaLoading(true);
              setError('');
              try {
                const data = await adminApi.connectWhatsApp();
                setWaStatus(data);
                setForm((prev) => ({ ...prev, evolutionEnabled: true }));
                setMessage(data.connected
                  ? 'WhatsApp già collegato.'
                  : 'Scansiona il QR con WhatsApp sul telefono del cliente.');
              } catch (err) {
                setError(err.message);
              } finally {
                setWaLoading(false);
              }
            }}
          >
            {waLoading ? 'Collegamento...' : 'Collega WhatsApp'}
          </Button>
          <Button
            type="button"
            color="warning"
            disabled={waLoading || !waStatus?.connected}
            onClick={async () => {
              if (!window.confirm('Disconnettere WhatsApp da questo numero?')) return;
              setWaLoading(true);
              try {
                const data = await adminApi.logoutWhatsApp();
                setWaStatus(data);
                setMessage('WhatsApp disconnesso.');
              } catch (err) {
                setError(err.message);
              } finally {
                setWaLoading(false);
              }
            }}
          >
            Disconnetti
          </Button>
        </Box>
        {waLoading && !waStatus?.qr && <CircularProgress size={22} sx={{ mb: 2 }} />}
        {waStatus?.qr && !waStatus?.connected && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              WhatsApp → Impostazioni → Dispositivi collegati → Collega un dispositivo
            </Typography>
            <Box
              component="img"
              src={waStatus.qr}
              alt="QR WhatsApp"
              sx={{ width: 260, height: 260, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            />
          </Box>
        )}
        <FormControlLabel
          control={<Switch checked={form.whatsappEnabled} onChange={setSwitch('whatsappEnabled')} />}
          label="Invia anche la conferma appuntamento su WhatsApp"
        />
        <TextField
          label="Messaggio di conferma"
          value={form.whatsappMessageBody}
          onChange={setField('whatsappMessageBody')}
          fullWidth
          multiline
          minRows={4}
          sx={{ mt: 2 }}
          helperText="Segnaposto: {{name}} {{datetime}} {{companyName}}"
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Email di conferma</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Email mittente" value={form.emailFrom} onChange={setField('emailFrom')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Oggetto email utente" value={form.userEmailSubject} onChange={setField('userEmailSubject')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Corpo email utente" value={form.userEmailBody} onChange={setField('userEmailBody')} fullWidth multiline minRows={3} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Oggetto email admin" value={form.adminEmailSubject} onChange={setField('adminEmailSubject')} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Corpo email admin" value={form.adminEmailBody} onChange={setField('adminEmailBody')} fullWidth multiline minRows={3} />
          </Grid>
        </Grid>
      </Paper>

      <Button type="submit" variant="contained">
        Salva
      </Button>
    </Box>
  );
}
